/**
 * Typed bindings for /api/inbox.
 *
 * Inbox = incoming user-DM stream for accounts the operator flagged
 * with inbox_enabled=true. Polling worker fills account_dialogs /
 * account_messages on a 5-min cadence; this module is the read/reply
 * surface for the web UI.
 */

import { useEffect, useRef } from "react";
import type { TFunction } from "i18next";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api, extractErrorDetail } from "./api";

/** Telegram's official service peer ("Telegram", login codes / 777000). */
export const TELEGRAM_SERVICE_PEER_ID = 777000;

/**
 * What the operator has decided about a lead, in the order the menus show it.
 * A dialog without a status has `lead_status: null` — "no status" is the
 * absence of one, not a seventh value.
 */
export const LEAD_STATUSES = [
  "new",
  "interested",
  "follow_up",
  "client",
  "not_interested",
  "closed",
] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];
/** The list filter: one status, or "none" for the dialogs without one. */
export type LeadStatusFilterValue = LeadStatus | "none";

export interface DialogSummary {
  id: string;
  account_id: string;
  account_label: string | null;
  peer_tg_id: number;
  peer_username: string | null;
  peer_first_name: string | null;
  peer_last_name: string | null;
  last_message_at: string | null;
  unread_count: number;
  archived: boolean;
  created_at: string;
  // True when the peer has a cached avatar → render
  // /api/inbox/dialogs/{id}/avatar, else colored initials.
  has_avatar?: boolean;
  lead_status: LeadStatus | null;
  /** When to write again — only ever set on a `follow_up` lead. */
  follow_up_at: string | null;
  /**
   * In a search: the message the dialog was found by, cut down around the
   * match. Null when it was found by the person or the account.
   */
  match_text?: string | null;
  /** Kept at the top of the list. */
  pinned: boolean;
  /** Set when the state of the dialog's account stands in the way of a reply. */
  reply_warning: ReplyWarning | null;
  /** How many notes the operators have left on the conversation. */
  notes_count: number;
}

/**
 * What to know about the account before typing a reply: `blocked` — it will
 * not go out, `caution` — it may not. The text is the server's, for the operator.
 */
export interface ReplyWarning {
  severity: "blocked" | "caution";
  text: string;
}

/** Has the day to write to this lead again come? */
export function isFollowUpDue(
  dialog: Pick<DialogSummary, "lead_status" | "follow_up_at">,
  now: number = Date.now(),
): boolean {
  return (
    dialog.lead_status === "follow_up" &&
    dialog.follow_up_at !== null &&
    Date.parse(dialog.follow_up_at) <= now
  );
}

/**
 * The message another one replies to, as far as the quote above it needs.
 * `direction` is null when that message is not stored (older than what the
 * poll has read): then all that is known is that it is a reply.
 */
export interface ReplyQuote {
  tg_message_id: number;
  direction: "in" | "out" | null;
  text: string | null;
  has_media: boolean;
}

export interface InboxMessage {
  id: string;
  tg_message_id: number;
  direction: "in" | "out";
  text: string | null;
  has_media: boolean;
  sent_at: string;
  was_read: boolean;
  was_replied_by_human: boolean;
  reply_to: ReplyQuote | null;
  /** The login of the operator who typed it; null on what no operator typed. */
  sent_by: string | null;
  /**
   * What the media is: photo, video, video_note, gif, voice, audio, document,
   * sticker, or "other" (a contact, a location — nothing to open). Null with
   * `has_media` set = stored before the kind was recorded.
   */
  media_kind: string | null;
  media_name: string | null;
  /** The emoji our account put on this message. */
  my_reaction: string | null;
  /**
   * The emoji the other side put on it, as last seen by the poll; "custom" for
   * a custom-emoji reaction, which has no character to show.
   */
  peer_reaction: string | null;
}

// A voice note, a round video and a photo are named by what they are: their
// file names ("voice.oga") say nothing. The rest go by the file's name.
const NAMED_BY_KIND = new Set(["voice", "video_note", "photo", "gif", "sticker"]);

/** What a message carries, in words: "Photo", "Voice message", a file's name. */
export function mediaLabel(
  message: Pick<InboxMessage, "media_kind" | "media_name">,
  t: TFunction,
): string {
  // A message stored before kinds were recorded only says "there was something".
  const kind = message.media_kind ?? "unknown";
  return (!NAMED_BY_KIND.has(kind) && message.media_name) || t(`inbox.media.kind.${kind}`);
}

/** The largest file a reply can carry — the server's limit, checked before the upload. */
export const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

/**
 * Telegram's standard reactions, in its order and its spelling — the server
 * takes no other.
 */
export const REACTIONS: readonly string[] = (
  "❤ 👍 👎 🔥 🥰 👏 😁 🤔 🤯 😱 🤬 😢 🎉 🤩 " +
  "🤮 💩 🙏 👌 🕊 🤡 🥱 🥴 😍 🐳 ❤‍🔥 🌚 🌭 💯 " +
  "🤣 ⚡ 🍌 🏆 💔 🤨 😐 🍓 🍾 💋 🖕 😈 😴 😭 " +
  "🤓 👻 👨‍💻 👀 🎃 🙈 😇 😨 🤝 ✍ 🤗 🫡 🎅 🎄 " +
  "☃ 💅 🤪 🗿 🆒 💘 🙉 🦄 😘 💊 🙊 😎 👾 🤷‍♂ " +
  "🤷 🤷‍♀ 😡"
).split(" ");
/** The row the picker opens with — the head of Telegram's list. */
export const QUICK_REACTIONS = REACTIONS.slice(0, 7);

// Telegram spells a few reactions without the selector that makes a browser
// draw them in colour: its "❤" comes out as a black heart. They are shown with
// the selector and sent without it.
const WANTS_SELECTOR = /[\u2764\u270D\u2603\u2640\u2642\u{1F54A}](?!\uFE0F)/gu;

/** A reaction as it is drawn on screen. */
export function showReaction(emoji: string): string {
  return emoji.replace(WANTS_SELECTOR, "$&\uFE0F");
}

export interface MessagesPage {
  dialog_id: string;
  items: InboxMessage[];
  has_more: boolean;
}

export interface UnreadCount {
  total: number;
  /** Follow-ups whose day has come. */
  follow_ups_due: number;
}

export interface InboxState {
  account_id: string;
  enabled: boolean;
}

const DIALOGS_KEY = "inbox_dialogs";
const MESSAGES_KEY = "inbox_messages";

export function useUnreadCount() {
  return useQuery<UnreadCount>({
    queryKey: ["inbox_unread"],
    queryFn: () => api.get<UnreadCount>("/api/inbox/unread-count"),
    // Sidebar polls — keep this lightweight.
    refetchInterval: 30_000,
  });
}

/** The whole working list in numbers, whatever is filtered on screen. */
export interface InboxTotals {
  dialogs: number;
  accounts: number;
  /** Conversations holding something unread. */
  unread: number;
  follow_ups_due: number;
}

export function useInboxTotals() {
  return useQuery<InboxTotals>({
    // Under "inbox_unread": what refreshes the sidebar badge refreshes this too.
    queryKey: ["inbox_unread", "totals"],
    queryFn: () => api.get<InboxTotals>("/api/inbox/totals"),
    refetchInterval: 30_000,
  });
}

export interface DialogFilter {
  account_id?: string;
  unread_only?: boolean;
  lead_status?: LeadStatusFilterValue;
  /** Only follow-ups whose day has come. */
  follow_up_due?: boolean;
  /** The search box: name, @username, Telegram id, message text, account. */
  q?: string;
  /** The archive instead of the working list. */
  archived?: boolean;
  /** One campaign's answers (its id), or "none" — those no campaign started. */
  campaign?: string;
  limit?: number;
  offset?: number;
}

export function useDialogs(filter: DialogFilter = {}) {
  return useQuery<DialogSummary[]>({
    queryKey: [DIALOGS_KEY, filter],
    queryFn: () => {
      const p = new URLSearchParams();
      if (filter.account_id) p.set("account_id", filter.account_id);
      if (filter.unread_only) p.set("unread_only", "true");
      if (filter.lead_status) p.set("lead_status", filter.lead_status);
      if (filter.follow_up_due) p.set("follow_up_due", "true");
      if (filter.q) p.set("q", filter.q);
      if (filter.archived) p.set("archived", "true");
      if (filter.campaign) p.set("campaign", filter.campaign);
      if (filter.limit != null) p.set("limit", String(filter.limit));
      if (filter.offset != null) p.set("offset", String(filter.offset));
      const qs = p.toString();
      return api.get<DialogSummary[]>(
        `/api/inbox/dialogs${qs ? `?${qs}` : ""}`,
      );
    },
    refetchInterval: 30_000,
    // A new filter or another letter in the search keeps the old rows on
    // screen until its own arrive, instead of blinking to "loading".
    placeholderData: keepPreviousData,
  });
}

/** The options of the campaign filter: the campaigns somebody answered. */
export interface InboxCampaigns {
  campaigns: { id: string; name: string; dialogs: number }[];
  /** Answered conversations that no campaign started. */
  without_campaign: number;
}

export function useInboxCampaigns() {
  return useQuery<InboxCampaigns>({
    queryKey: ["inbox_campaigns"],
    queryFn: () => api.get<InboxCampaigns>("/api/inbox/campaigns"),
    // The counts follow the answers coming in; a minute behind is fine.
    refetchInterval: 60_000,
  });
}

/** Where the person on the other side of a dialog came from. */
export interface LeadContext {
  dialog_id: string;
  /**
   * Who opened the conversation: a campaign's message, the person themselves,
   * or our account outside any campaign.
   */
  started_by: "campaign" | "lead" | "us";
  campaign: { id: string; name: string; strategy: string } | null;
  /** The campaign's first message here — what the person is answering. */
  first_message: string | null;
  first_message_has_media: boolean;
  first_contact_at: string | null;
  /** The channel the audience first saw the person in, or the upload's label. */
  source: string | null;
  source_kind: "channel" | "import" | null;
  in_audience: boolean;
  is_premium: boolean | null;
  language_code: string | null;
  channels: string[];
  channels_total: number;
}

/** Read from our own tables — opening a dialog asks nothing of Telegram. */
export function useLeadContext(dialogId: string | null) {
  return useQuery<LeadContext>({
    queryKey: ["inbox_context", dialogId],
    enabled: dialogId !== null,
    queryFn: () => api.get<LeadContext>(`/api/inbox/dialogs/${dialogId}/context`),
    staleTime: 60_000,
  });
}

/** An operator's note on a conversation. Internal: it never goes to Telegram. */
export interface DialogNote {
  id: string;
  text: string;
  /** The author's login; null once that user is gone. */
  author: string | null;
  created_at: string;
}

const NOTES_KEY = "inbox_notes";

export function useDialogNotes(dialogId: string) {
  return useQuery<DialogNote[]>({
    queryKey: [NOTES_KEY, dialogId],
    queryFn: () => api.get<DialogNote[]>(`/api/inbox/dialogs/${dialogId}/notes`),
    // A colleague may be writing in the same conversation.
    refetchInterval: 60_000,
  });
}

export function useAddDialogNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dialogId, text }: { dialogId: string; text: string }) =>
      api.post<DialogNote>(`/api/inbox/dialogs/${dialogId}/notes`, { text }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: [NOTES_KEY, vars.dialogId] });
      // The list marks the dialogs that have notes.
      qc.invalidateQueries({ queryKey: [DIALOGS_KEY] });
    },
  });
}

export function useDeleteDialogNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dialogId, noteId }: { dialogId: string; noteId: string }) =>
      api.delete<void>(`/api/inbox/dialogs/${dialogId}/notes/${noteId}`),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: [NOTES_KEY, vars.dialogId] });
      qc.invalidateQueries({ queryKey: [DIALOGS_KEY] });
    },
  });
}

export function useDialogMessages(
  dialogId: string | null,
  opts: { limit?: number; before_id?: string } = {},
) {
  return useQuery<MessagesPage>({
    queryKey: [MESSAGES_KEY, dialogId, opts],
    enabled: dialogId !== null,
    queryFn: () => {
      const p = new URLSearchParams();
      if (opts.limit != null) p.set("limit", String(opts.limit));
      if (opts.before_id) p.set("before_id", opts.before_id);
      const qs = p.toString();
      return api.get<MessagesPage>(
        `/api/inbox/dialogs/${dialogId}/messages${qs ? `?${qs}` : ""}`,
      );
    },
    refetchInterval: 15_000,
  });
}

export function useReplyToDialog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      dialogId,
      text,
      replyTo,
      file,
      materialId,
    }: {
      dialogId: string;
      text: string;
      /** `tg_message_id` of the message being answered — sent as a Telegram reply. */
      replyTo?: number;
      /** A file from the operator's computer; `text` is then its caption. */
      file?: File;
      /** A file material of the content library, sent the same way. */
      materialId?: string;
    }) => {
      if (materialId) {
        return api.post<InboxMessage>(`/api/inbox/dialogs/${dialogId}/reply-material`, {
          material_id: materialId,
          text,
          reply_to_tg_message_id: replyTo ?? null,
        });
      }
      if (!file) {
        return api.post<InboxMessage>(`/api/inbox/dialogs/${dialogId}/reply`, {
          text,
          reply_to_tg_message_id: replyTo ?? null,
        });
      }
      const form = new FormData();
      form.set("file", file);
      form.set("text", text);
      if (replyTo != null) form.set("reply_to_tg_message_id", String(replyTo));
      return api.upload<InboxMessage>(`/api/inbox/dialogs/${dialogId}/reply-file`, form);
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: [MESSAGES_KEY, vars.dialogId] });
      qc.invalidateQueries({ queryKey: [DIALOGS_KEY] });
      qc.invalidateQueries({ queryKey: ["inbox_unread"] });
    },
  });
}

/**
 * The file a message carries. It is not kept on our side: the request fetches
 * it from Telegram through the dialog's account, so it is made only when the
 * operator asks for the file.
 */
export async function fetchMessageMedia(dialogId: string, messageId: string): Promise<Blob> {
  const response = await fetch(`/api/inbox/dialogs/${dialogId}/messages/${messageId}/media`, {
    credentials: "include",
  });
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    throw new ApiError(response.status, extractErrorDetail(body, response.statusText), body);
  }
  return response.blob();
}

/** Put our account's reaction on a message, change it, or (null) take it off. */
export function useSetReaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      dialogId,
      messageId,
      emoji,
    }: {
      dialogId: string;
      messageId: string;
      emoji: string | null;
    }) =>
      api.put<InboxMessage>(`/api/inbox/dialogs/${dialogId}/messages/${messageId}/reaction`, {
        emoji,
      }),
    onSuccess: (updated, vars) => {
      qc.setQueriesData<MessagesPage>({ queryKey: [MESSAGES_KEY, vars.dialogId] }, (page) =>
        page
          ? { ...page, items: page.items.map((m) => (m.id === updated.id ? updated : m)) }
          : page,
      );
    },
  });
}

/**
 * How long to wait before asking Telegram about a live conversation again, or
 * null when it is no longer live. `elapsedMs` counts from the last sign of
 * life — the operator's reply, or an answer that a refresh brought in.
 *
 * The regular poll takes accounts in turn, so the answer to a reply could sit
 * unseen for many minutes. For a few minutes after a reply that one chat is
 * asked for directly: often at first, then less, then not at all — every
 * request is a connection of the account, and a conversation that has gone
 * quiet is not worth one.
 */
export function nextRefreshDelay(elapsedMs: number): number | null {
  if (elapsedMs < 3 * 60_000) return 30_000;
  if (elapsedMs < 10 * 60_000) return 60_000;
  return null;
}

function useRefreshDialog() {
  const qc = useQueryClient();
  return useMutation({
    // A background beat: a busy or benched account must not raise a toast on
    // every one of them.
    meta: { silentErrors: true },
    mutationFn: (dialogId: string) =>
      api.post<{ new_messages: number }>(`/api/inbox/dialogs/${dialogId}/refresh`),
    onSuccess: (res, dialogId) => {
      if (res.new_messages === 0) return;
      qc.invalidateQueries({ queryKey: [MESSAGES_KEY, dialogId] });
      qc.invalidateQueries({ queryKey: [DIALOGS_KEY] });
      qc.invalidateQueries({ queryKey: ["inbox_unread"] });
    },
  });
}

/**
 * Keep one conversation fresh while it is live — see `nextRefreshDelay`.
 * `liveSince` is when the operator last replied here (ms), or null: merely
 * opening a dialog reads what is stored and asks Telegram nothing. A beat that
 * falls while the tab is hidden is skipped, not postponed.
 */
export function useLiveDialogRefresh(
  dialogId: string | null,
  liveSince: number | null,
  onNewMessages?: () => void,
) {
  const refresh = useRefreshDialog();
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const onNewRef = useRef(onNewMessages);
  onNewRef.current = onNewMessages;

  useEffect(() => {
    if (dialogId === null || liveSince === null) return;
    let aliveSince = liveSince;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const schedule = () => {
      const delay = nextRefreshDelay(Date.now() - aliveSince);
      if (delay !== null) timer = setTimeout(beat, delay);
    };
    const beat = async () => {
      if (cancelled) return;
      if (document.visibilityState === "visible") {
        try {
          const res = await refreshRef.current.mutateAsync(dialogId);
          if (res.new_messages > 0) {
            aliveSince = Date.now();
            onNewRef.current?.();
          }
        } catch {
          /* the account is busy or benched — the next beat tries again */
        }
      }
      if (!cancelled) schedule();
    };

    schedule();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [dialogId, liveSince]);
}

export function useMarkDialogRead() {
  const qc = useQueryClient();
  return useMutation({
    // Fired by merely opening a dialog: a read-only role is refused, and that
    // must not raise a toast on every click.
    meta: { silentErrors: true },
    mutationFn: (dialogId: string) =>
      api.post<void>(`/api/inbox/dialogs/${dialogId}/mark-read`),
    onSuccess: (_d, dialogId) => {
      qc.invalidateQueries({ queryKey: [MESSAGES_KEY, dialogId] });
      qc.invalidateQueries({ queryKey: [DIALOGS_KEY] });
      qc.invalidateQueries({ queryKey: ["inbox_unread"] });
    },
  });
}

export function useArchiveDialog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      dialogId,
      unarchive,
    }: {
      dialogId: string;
      unarchive?: boolean;
    }) =>
      api.post<void>(
        `/api/inbox/dialogs/${dialogId}/archive${unarchive ? "?unarchive=true" : ""}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [DIALOGS_KEY] });
      qc.invalidateQueries({ queryKey: ["inbox_unread"] });
    },
  });
}

/** Change what the operator owns about a dialog. Nothing goes to Telegram. */
export function useUpdateDialog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      dialogId,
      ...patch
    }: {
      dialogId: string;
      lead_status?: LeadStatus | null;
      /** ISO time; setting one makes the lead a follow-up. */
      follow_up_at?: string | null;
      pinned?: boolean;
      /** true = "come back to this", false = read. */
      unread?: boolean;
    }) => api.patch<DialogSummary>(`/api/inbox/dialogs/${dialogId}`, patch),
    onSuccess: (updated) => {
      // Show it at once wherever the dialog is listed, then let each list decide
      // whether it still belongs there — a status filter may now leave it out.
      qc.setQueriesData<DialogSummary[]>({ queryKey: [DIALOGS_KEY] }, (rows) =>
        rows?.map((d) => (d.id === updated.id ? updated : d)),
      );
      qc.invalidateQueries({ queryKey: [DIALOGS_KEY] });
      qc.invalidateQueries({ queryKey: ["inbox_unread"] });
    },
  });
}

export function useInboxState(accountId: string | null) {
  return useQuery<InboxState>({
    queryKey: ["inbox_state", accountId],
    enabled: accountId !== null,
    queryFn: () => api.get<InboxState>(`/api/inbox/state/${accountId}`),
  });
}

export function useSetInboxEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      accountId,
      enabled,
    }: {
      accountId: string;
      enabled: boolean;
    }) =>
      api.post<InboxState>(`/api/inbox/state/${accountId}`, { enabled }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["inbox_state", vars.accountId] });
      qc.invalidateQueries({ queryKey: ["accounts"] });
    },
  });
}

/**
 * Watch the unread counter and fire a browser notification when it
 * climbs vs the previous poll. Designed for the app shell — mount once,
 * not per-page, otherwise multiple instances will all ping the same
 * arrival.
 *
 * First successful read of the counter is treated as the BASELINE, NOT
 * an arrival. Otherwise opening the app with 5 already-unread messages
 * would dump 5 notifications. Subsequent increases fire one notification
 * with the delta in the body.
 *
 * Permission is requested lazily on the first delta — we don't pop the
 * native permission prompt the moment the user logs in. If permission
 * was denied at the OS/browser level we silently fall back to just the
 * sidebar badge.
 */
export function useInboxNotifications(opts: { onClick?: () => void } = {}) {
  const { data } = useUnreadCount();
  const baseline = useRef<number | null>(null);
  const lastSeen = useRef<number | null>(null);
  const onClickRef = useRef(opts.onClick);
  onClickRef.current = opts.onClick;

  useEffect(() => {
    if (data?.total == null) return;
    const total = data.total;

    if (baseline.current === null) {
      baseline.current = total;
      lastSeen.current = total;
      return;
    }

    const prev = lastSeen.current ?? total;
    lastSeen.current = total;

    if (total <= prev) return;

    const delta = total - prev;

    if (typeof Notification === "undefined") return;
    if (document.visibilityState === "visible") return; // tab open — sidebar badge is enough

    const fire = () => {
      try {
        const n = new Notification("Inbox", {
          body:
            delta === 1
              ? "1 new message"
              : `${delta} new messages`,
          tag: "inbox-unread",
        });
        n.onclick = () => {
          window.focus();
          onClickRef.current?.();
          n.close();
        };
      } catch {
        /* iframe / sandbox / OS-disabled — ignore */
      }
    };

    if (Notification.permission === "granted") {
      fire();
    } else if (Notification.permission === "default") {
      Notification.requestPermission().then((p) => {
        if (p === "granted") fire();
      });
    }
  }, [data?.total]);
}

export function useInboxTick({ silent = false }: { silent?: boolean } = {}) {
  const qc = useQueryClient();
  return useMutation({
    // The background auto-poll must not raise a toast every interval.
    meta: silent ? { silentErrors: true } : undefined,
    mutationFn: () =>
      api.post<{
        accounts_processed: number;
        accounts_skipped: number;
        new_messages: number;
        new_dialogs: number;
        errors: number;
      }>("/api/inbox/tick", { use_live_telethon: true, batch_size: 10 }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [DIALOGS_KEY] });
      qc.invalidateQueries({ queryKey: ["inbox_unread"] });
    },
  });
}

/**
 * Auto-poll: while `active` (an inbox surface is open) AND the tab is visible,
 * fire a live Telethon tick on an interval so replies surface without the
 * operator pressing "Refresh now". The dialog list itself refetches from the DB
 * every 30s — this just keeps the DB fresh by pulling from Telegram.
 *
 * Deliberately gated:
 * - paused when the tab is hidden (don't burn TG quota in the background — the
 *   5-min background cron covers that case);
 * - never overlaps an in-flight tick;
 * - does NOT fire immediately on mount (avoids a tick storm from opening/closing
 *   the drawer); the first auto-tick lands one interval later.
 */
export function useAutoInboxTick(active: boolean, intervalMs = 120_000) {
  const tick = useInboxTick({ silent: true });
  const tickRef = useRef(tick);
  tickRef.current = tick;

  useEffect(() => {
    if (!active) return;
    let timer: ReturnType<typeof setInterval> | null = null;

    const run = () => {
      if (document.visibilityState !== "visible") return;
      if (tickRef.current.isPending) return;
      tickRef.current.mutate();
    };
    const start = () => {
      if (!timer) timer = setInterval(run, intervalMs);
    };
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        run(); // catch up on whatever arrived while hidden
        start();
      } else {
        stop();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    if (document.visibilityState === "visible") start();

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      stop();
    };
  }, [active, intervalMs]);
}
